import React, { useEffect, useMemo, useRef, useState } from 'react';
import s from './chatStyles';
import { SendHorizontal, Loader2, Bot, Clock, Mail, AlertTriangle, Keyboard, Zap, ImagePlus, Megaphone } from 'lucide-react';
import { QuickReplyPicker, QuickReplyManager, useQuickReplies, filterReplies, applyVariables } from './QuickReplies';
import { AttachImageModal, PromotionPickerModal, IMAGE_TYPES } from './ImageModals';

/**
 * Caja de texto para responder como asesor. Respuestas rápidas con el botón ⚡
 * o escribiendo "/" al inicio (se filtran con lo que se escriba después).
 * Botón + (imagen JPG/PNG) y botón Promociones (biblioteca): el texto escrito
 * pasa a ser el texto de la imagen.
 */
export default function Composer({
  conv, text, onChange, onSend, sending, sendError, windowClosed, textareaRef, onSendTemplate,
  isAdmin, agentName, onSendImage, onSendPromotion,
}) {
  // Con SARA atendiendo no se escribe: primero hay que tomar el caso (pausa a SARA)
  const saraActive = ['bot', 'closed'].includes(conv?.status);
  const blocked = windowClosed || saraActive;
  const disabled = !text.trim() || sending || blocked;

  const quick = useQuickReplies();
  const [manualOpen, setManualOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [managing, setManaging] = useState(false);
  const [index, setIndex] = useState(0);
  const fileRef = useRef(null);
  const [attachFile, setAttachFile] = useState(null);
  const [promosOpen, setPromosOpen] = useState(false);
  const canImage = !blocked && conv?.canal === 'whatsapp';

  // "/texto" en una sola línea abre el selector filtrado por "texto"
  const slashQuery = text.startsWith('/') && !text.includes('\n') ? text.slice(1) : null;
  const open = !blocked && ((slashQuery !== null && !dismissed) || manualOpen);
  const query = slashQuery ?? '';
  const items = useMemo(() => filterReplies(quick.items, query), [quick.items, query]);

  useEffect(() => { setIndex(0); }, [query, open]);
  useEffect(() => { if (slashQuery === null) setDismissed(false); }, [slashQuery]);

  function close() { setManualOpen(false); setDismissed(true); }

  function pick(r) {
    const value = applyVariables(r.body, { contactName: conv?.contact_name, agentName });
    // Desde "/": reemplaza lo escrito. Desde ⚡: se agrega a lo que ya había.
    if (slashQuery !== null || !text.trim()) onChange(value);
    else onChange(`${text.replace(/\s+$/, '')}\n${value}`);
    setManualOpen(false);
    setDismissed(true);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }

  function onKeyDown(e) {
    if (open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIndex(i => Math.min(i + 1, Math.max(items.length - 1, 0))); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (items[index]) pick(items[index]);
        else close();
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
  }

  return (
    <div style={s.inputArea}>
      <div style={s.inputInner}>
        <div style={{ ...s.inputBox, position: 'relative' }}>
          {open && (
            <QuickReplyPicker items={items} query={query} index={index} onPick={pick}
              onManage={() => { close(); setManaging(true); }} onClose={close} />
          )}
          <button
            type="button"
            className="ui-hover"
            style={{ ...s.quickBtn, ...(open ? s.quickBtnActive : {}) }}
            onClick={() => { setManualOpen(o => !o); setDismissed(false); textareaRef.current?.focus(); }}
            disabled={blocked}
            title="Respuestas rápidas (también escribiendo / )"
            aria-label="Respuestas rápidas"
            aria-expanded={open}
          >
            <Zap size={18} />
          </button>
          {onSendImage && (
            <>
              <button
                type="button" className="ui-hover" style={s.quickBtn}
                onClick={() => fileRef.current?.click()} disabled={!canImage}
                title="Enviar imagen (JPG o PNG, hasta 5 MB)" aria-label="Enviar imagen"
              >
                <ImagePlus size={18} />
              </button>
              <input ref={fileRef} type="file" accept={IMAGE_TYPES.join(',')} hidden
                onChange={e => { setAttachFile(e.target.files?.[0] || null); e.target.value = ''; }} />
            </>
          )}
          {onSendPromotion && (
            <button
              type="button" className="ui-hover" style={s.quickBtn}
              onClick={() => setPromosOpen(true)} disabled={!canImage}
              title="Promociones: imágenes de la biblioteca" aria-label="Promociones"
            >
              <Megaphone size={18} />
            </button>
          )}
          <textarea
            ref={textareaRef}
            style={{ ...s.textarea, opacity: blocked ? 0.5 : 1 }}
            disabled={blocked}
            placeholder={saraActive
              ? 'SARA está atendiendo: usa "Atender yo" o asigna la conversación para escribir'
              : windowClosed
                ? 'Ventana de 24 h cerrada: solo se puede escribir con una plantilla'
                : 'Escribe un mensaje… (Enter envía · / para respuestas rápidas)'}
            value={text}
            onChange={e => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
          />
          <button
            className="ui-primary"
            style={{ ...s.sendBtn, opacity: disabled ? 0.45 : 1 }}
            onClick={onSend}
            disabled={disabled}
            aria-label="Enviar mensaje"
            title="Enviar"
          >
            {sending ? <Loader2 size={18} className="ui-spin" /> : <SendHorizontal size={19} />}
          </button>
        </div>
        {attachFile && (
          <AttachImageModal file={attachFile} initialCaption={text.trim()} onClose={() => setAttachFile(null)}
            onSend={async (file, caption) => { await onSendImage(file, caption); onChange(''); }} />
        )}
        {promosOpen && (
          <PromotionPickerModal initialCaption={text.trim()} onClose={() => setPromosOpen(false)}
            onSend={async (id, caption) => { await onSendPromotion(id, caption); onChange(''); }} />
        )}
        {managing && (
          <QuickReplyManager items={quick.items} isAdmin={isAdmin} onClose={() => setManaging(false)} onChanged={quick.reload} />
        )}
        {saraActive && (
          <div style={s.infoNote}>
            <Bot size={16} color="var(--accent)" style={{ flexShrink: 0 }} />
            <span>SARA está atendiendo esta conversación. Al tomarla ("Atender yo" o asignándola) SARA se pausa; al cerrarla, SARA retoma.</span>
          </div>
        )}
        {windowClosed && !saraActive && (
          <div style={s.windowNote}>
            <Clock size={16} color="var(--warning)" style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>Pasaron más de 24 horas desde el último mensaje del cliente. WhatsApp solo permite escribirle con una plantilla aprobada.</span>
            {onSendTemplate && (
              <button className="ui-primary" style={s.primaryBtn} onClick={onSendTemplate}><Mail size={14} /> Enviar plantilla</button>
            )}
          </div>
        )}
        {sendError && !blocked && <div style={{ ...s.sendErrorNote, display: 'flex', gap: 8, alignItems: 'center' }}><AlertTriangle size={15} /> {sendError}</div>}
        <div style={s.inputNote}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Keyboard size={12} /> Enter envía · Shift+Enter nueva línea</span>
          <span style={{ color: conv?.canal === 'whatsapp' ? 'var(--success)' : 'var(--text-3)' }}>
            {conv?.canal === 'whatsapp' ? 'Se enviará por WhatsApp' : 'Solo se guarda en el historial'}
          </span>
        </div>
      </div>
    </div>
  );
}
